import React, { createContext, PropsWithChildren, useCallback, useContext, useMemo, useRef, useState } from 'react'
import { PurchaseAssistOffer, PurchaseAssistRequest } from '@prometheus/types'
import { PurchaseAssistModal } from './PurchaseAssistModal'
import { acceptedRequest, isOfferActionable } from './purchaseAssist'

interface PendingOffer {
  offer: PurchaseAssistOffer
  actionLabel?: string
}

export interface PurchaseAssistContextValue {
  /**
   * Show the offer and resolve with the flags to retry the action with, or
   * `null` if the player declined. Resolves `null` immediately when no
   * provider is mounted so lobby-side callers degrade to the plain error.
   */
  requestAssist: (offer: PurchaseAssistOffer, actionLabel?: string) => Promise<PurchaseAssistRequest | null>
}

const PurchaseAssistContext = createContext<PurchaseAssistContextValue>({
  requestAssist: async () => null,
})

export const usePurchaseAssist = () => useContext(PurchaseAssistContext)

/**
 * Turns a refused purchase into a confirmation popup.
 *
 * When the server refuses a buy because the player is short on cash or crew
 * it attaches an offer describing what it could do instead. This provider
 * shows that offer; accepting resolves the promise the action hook is
 * parked on, which re-runs the SAME action with `auto_finance` / `auto_hire`
 * set — so the withdrawal, the hire and the purchase all land in one atomic
 * server transaction rather than as separate half-applied steps.
 */
export const PurchaseAssistProvider = ({ children }: PropsWithChildren<{}>) => {
  const [pending, setPending] = useState<PendingOffer | null>(null)
  const [submitting, setSubmitting] = useState(false)
  const resolveRef = useRef<((request: PurchaseAssistRequest | null) => void) | null>(null)

  const settle = useCallback((request: PurchaseAssistRequest | null) => {
    const resolve = resolveRef.current
    resolveRef.current = null
    setPending(null)
    setSubmitting(false)
    resolve?.(request)
  }, [])

  const requestAssist = useCallback((offer: PurchaseAssistOffer, actionLabel?: string) => {
    if (!isOfferActionable(offer)) {
      return Promise.resolve(null)
    }
    // Only one offer can be on screen; a second refusal arriving mid-decision
    // (a queued click, say) declines rather than stomping the open popup.
    if (resolveRef.current) {
      return Promise.resolve(null)
    }
    return new Promise<PurchaseAssistRequest | null>((resolve) => {
      resolveRef.current = resolve
      setPending({ offer, actionLabel })
    })
  }, [])

  const onAccept = useCallback(() => {
    if (!pending) {
      return
    }
    // Keep the buttons dead while the retry is in flight — the modal is
    // unmounted by `settle`, but the state guards a double-tap racing it.
    setSubmitting(true)
    settle(acceptedRequest(pending.offer))
  }, [pending, settle])

  const onCancel = useCallback(() => settle(null), [settle])

  const value = useMemo(() => ({ requestAssist }), [requestAssist])

  return (
    <PurchaseAssistContext.Provider value={value}>
      {children}
      {pending && (
        <PurchaseAssistModal
          offer={pending.offer}
          actionLabel={pending.actionLabel}
          submitting={submitting}
          onAccept={onAccept}
          onCancel={onCancel}
        />
      )}
    </PurchaseAssistContext.Provider>
  )
}

export default PurchaseAssistProvider
